import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Save, X } from "lucide-react";
import { getAllBranches } from "../../../api/hrm/branches/info";
import {
  createReceipt,
  updateReceipt,
} from "../../../api/finance/receipts/receiptRequests";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";

const PAYER_TYPES = [
  { value: "student", label: "Học viên" },
  { value: "school", label: "Trường học" },
  { value: "customer", label: "Doanh nghiệp" },
  { value: "other", label: "Khác" },
];

const PAYMENT_METHODS = [
  { value: "Cash", label: "Tiền mặt" },
  { value: "Bank Transfer", label: "Chuyển khoản" },
  { value: "Card", label: "Thẻ" },
  { value: "Other", label: "Khác" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Nháp" },
  { value: "posted", label: "Đã ghi nhận" },
  { value: "voided", label: "Hủy" },
];

const INPUT_CLASS =
  "w-full h-11 rounded-xl border border-gray-200 px-3 text-sm outline-none focus:border-red-400 focus:ring-4 focus:ring-red-100";

const emptyForm = {
  receipt_date: new Date().toISOString().slice(0, 10),
  payer_type: "student",
  payer_name: "",
  payer_id: "",
  branch_id: "",
  amount: "",
  payment_method: "Bank Transfer",
  reason: "",
  status: "posted",
};

const ReceiptCreateModal = ({ isOpen, onClose, receipt, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);

  const isEdit = !!receipt?.id;

  useEffect(() => {
    if (!isOpen) return;
    const loadBranches = async () => {
      try {
        const res = await getAllBranches();
        const items = Array.isArray(res) ? res : res?.items || res?.data || [];
        setBranches(items);
      } catch (error) {
        console.error("Load branches failed:", error);
        setBranches([]);
      }
    };
    loadBranches();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!receipt) {
      setForm(emptyForm);
      return;
    }
    setForm({
      receipt_date: receipt.receipt_date ? String(receipt.receipt_date).slice(0, 10) : emptyForm.receipt_date,
      payer_type: receipt.payer_type || "student",
      payer_name: receipt.payer_name || "",
      payer_id: receipt.payer_id || "",
      branch_id: receipt.branch_id || "",
      amount: receipt.amount ?? "",
      payment_method: receipt.payment_method || "Bank Transfer",
      reason: receipt.reason || "",
      status: receipt.status || "posted",
    });
  }, [isOpen, receipt]);

  const title = useMemo(() => (isEdit ? "Cập nhật phiếu thu" : "Lập phiếu thu mới"), [isEdit]);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.payer_name.trim()) return showErrorToast("Vui lòng nhập người nộp tiền.");
    if (!Number(form.amount) || Number(form.amount) <= 0) {
      return showErrorToast("Số tiền phải lớn hơn 0.");
    }
    if (!form.reason.trim()) return showErrorToast("Vui lòng nhập lý do thu.");

    const payload = {
      receipt_date: form.receipt_date,
      payer_type: form.payer_type,
      payer_name: form.payer_name.trim(),
      payer_id: form.payer_id || null,
      branch_id: form.branch_id || null,
      amount: Number(form.amount),
      payment_method: form.payment_method,
      reason: form.reason.trim(),
      status: form.status,
    };

    try {
      setSaving(true);
      if (isEdit) {
        await updateReceipt(receipt.id, payload);
        showSuccessToast("Cập nhật phiếu thu thành công.");
      } else {
        await createReceipt(payload);
        showSuccessToast("Tạo phiếu thu thành công.");
      }
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error("Save receipt failed:", error);
      showErrorToast(error?.response?.data?.message || "Không thể lưu phiếu thu.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[130] bg-gray-900/50 backdrop-blur-[2px]"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[140] flex items-center justify-center p-4 pointer-events-none"
          >
            <form
              onSubmit={handleSubmit}
              className="w-full max-w-3xl bg-white rounded-3xl border border-gray-100 shadow-2xl overflow-hidden pointer-events-auto"
            >
              <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-black text-gray-800 tracking-tight uppercase">{title}</h3>
                  <p className="text-[11px] text-gray-500 font-bold uppercase tracking-wider mt-1">
                    Nhập thông tin phiếu thu theo dữ liệu thực
                  </p>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="h-10 w-10 rounded-xl border border-gray-200 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                >
                  <X size={18} className="mx-auto" />
                </button>
              </div>

              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
                <Field label="Ngày thu">
                  <input
                    type="date"
                    value={form.receipt_date}
                    onChange={(e) => updateField("receipt_date", e.target.value)}
                    className={INPUT_CLASS}
                    required
                  />
                </Field>

                <Field label="Chi nhánh">
                  <select
                    value={form.branch_id}
                    onChange={(e) => updateField("branch_id", e.target.value)}
                    className={INPUT_CLASS}
                  >
                    <option value="">Theo chi nhánh người tạo</option>
                    {branches.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Đối tượng nộp">
                  <select
                    value={form.payer_type}
                    onChange={(e) => updateField("payer_type", e.target.value)}
                    className={INPUT_CLASS}
                    required
                  >
                    {PAYER_TYPES.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Người nộp tiền">
                  <input
                    value={form.payer_name}
                    onChange={(e) => updateField("payer_name", e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="Nhập họ tên / đơn vị"
                    required
                  />
                </Field>

                <Field label="Số tiền (VNĐ)">
                  <input
                    type="number"
                    min="1"
                    value={form.amount}
                    onChange={(e) => updateField("amount", e.target.value)}
                    className={INPUT_CLASS}
                    placeholder="0"
                    required
                  />
                </Field>

                <Field label="Phương thức">
                  <select
                    value={form.payment_method}
                    onChange={(e) => updateField("payment_method", e.target.value)}
                    className={INPUT_CLASS}
                    required
                  >
                    {PAYMENT_METHODS.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Trạng thái">
                  <select
                    value={form.status}
                    onChange={(e) => updateField("status", e.target.value)}
                    className={INPUT_CLASS}
                    required
                  >
                    {STATUS_OPTIONS.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </Field>

                <div />

                <Field label="Lý do thu" className="md:col-span-2">
                  <textarea
                    rows={3}
                    value={form.reason}
                    onChange={(e) => updateField("reason", e.target.value)}
                    className={`${INPUT_CLASS} h-auto py-2.5 resize-none`}
                    placeholder="Mô tả lý do thu"
                    required
                  />
                </Field>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-xs uppercase tracking-wider"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2.5 rounded-xl bg-red-700 text-white font-black text-xs uppercase tracking-wider flex items-center gap-2 disabled:opacity-70"
                >
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  {isEdit ? "Lưu thay đổi" : "Tạo phiếu thu"}
                </button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

const Field = ({ label, children, className = "" }) => (
  <div className={className}>
    <label className="text-[11px] font-black uppercase tracking-wider text-gray-400 mb-1.5 block">
      {label}
    </label>
    {children}
  </div>
);

export default ReceiptCreateModal;
